/**
 * AdviceService
 *
 * This service handles API calls related to advice and conversation resources.
 * It provides methods for listing, fetching by ID, creating, updating, and streaming advice.
 *
 * @module advice.service
 */

import { Session } from 'next-auth';

import {
  Advice,
  AdviceItem,
  AdviceItemDto,
  AdviceListResponse,
  ConversationResource,
  AdviceFeedbackData,
  FormData,
  FormAnswer,
  AdviceItemStatus,
} from '@/types';
import { API_CONFIG } from '@/lib/config/api.config';
import { apiService } from '@/lib/services/api.service';
import { logger } from '@/lib/utils/logger';
import {
  trackMetricsEvent,
  MetricsCategory,
  MetricsAction,
} from '@/lib/utils/metrics.utils';
import { bedrockService, BedrockOptions } from '@/lib/services/bedrock.service';
// import { createStructuredOutputPrompt } from '../prompts/bedrock-prompts';

/**
 * AdviceService class encapsulates API calls related to advice and conversations.
 */
export class AdviceService {
  private pendingRequests = new Map<string, Promise<any>>();
  private cache = new Map<string, { data: any; timestamp: number }>();
  private CACHE_TTL = 30000; // 30 seconds TTL
  private activeStreams = new Map<
    string,
    {
      controller: AbortController;
      cleanup: () => void;
    }
  >();
  private streamCache = new Map<
    string,
    {
      chunks: any[];
      finalResponse: any;
      timestamp: number;
    }
  >();
  private hasReceivedFirstChunk = false;

  /**
   * Generates a cache key based on a provided key and the current TTL.
   *
   * @param {string} key - The base key.
   * @returns {string} The cache key.
   */
  private getCacheKey(key: string): string {
    return `${key}-${Math.floor(Date.now() / this.CACHE_TTL)}`;
  }

  /**
   * Deduplicates requests by caching responses.
   *
   * @param {string} key - The key for caching.
   * @param {Promise<T>} request - The request promise.
   * @returns {Promise<NonNullable<T>>} A promise that resolves with the data.
   */
  private dedupeRequest<T>(
    key: string,
    request: Promise<T>
  ): Promise<NonNullable<T>> {
    const cacheKey = this.getCacheKey(key);
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      if (cached.data === null) throw new Error(`No data found for ${key}`);
      return Promise.resolve(cached.data);
    }
    const pending = this.pendingRequests.get(cacheKey);
    if (pending) return pending as Promise<NonNullable<T>>;
    const reqPromise = request
      .then((data) => {
        if (data === null) throw new Error(`No data found for ${key}`);
        this.cache.set(cacheKey, { data, timestamp: Date.now() });
        this.pendingRequests.delete(cacheKey);
        return data;
      })
      .catch((err) => {
        this.pendingRequests.delete(cacheKey);
        throw err;
      });
    this.pendingRequests.set(cacheKey, reqPromise);
    return reqPromise as Promise<NonNullable<T>>;
  }

  /**
   * Retrieves a list of advices.
   *
   * @param {Session} session - The user session.
   * @returns {Promise<AdviceListResponse>} The advice list response.
   */
  async list(session: Session): Promise<AdviceListResponse | null> {
    const key = 'list';
    const startTime = Date.now();

    try {
      // Check cache first
      const cacheKey = this.getCacheKey(key);
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        logger.debug('[AdviceService] Returning list from cache', { cacheKey });
        if (cached.data === null) throw new Error(`No data found for ${key}`);
        return Promise.resolve(cached.data);
      }

      // Check for pending request
      const pending = this.pendingRequests.get(cacheKey);
      if (pending) {
        logger.debug('[AdviceService] Returning list from pending request', { cacheKey });
        return pending as Promise<AdviceListResponse | null>;
      }

      logger.info('[AdviceService] Fetching advice list from API');
      // Make new request
      const request = apiService.get<AdviceListResponse>(
        API_CONFIG.ENDPOINTS.ADVICE.LIST,
        session
      );
      this.pendingRequests.set(cacheKey, request);

      const response = await request;

      // Cache the response
      this.cache.set(cacheKey, { data: response, timestamp: Date.now() });
      this.pendingRequests.delete(cacheKey);

      logger.info('[AdviceService] Advice list fetched successfully');
      return response;
    } catch (error: any) {
      logger.warn('[AdviceService] List request failed:', {
        error: error.message,
        status: error.response?.status,
        duration: `${Date.now() - startTime}ms`,
      });
      this.pendingRequests.delete(this.getCacheKey(key));
      return null;
    }
  }

  /**
   * Retrieves a conversation by ID.
   *
   * @param {string} id - The conversation ID.
   * @param {Session} session - The user session.
   * @returns {Promise<ConversationResource>} The conversation resource.
   */
  async getById(id: string, session: Session): Promise<ConversationResource> {
    const startTime = Date.now();
    logger.info('[AdviceService] Fetching conversation by ID', { id });
    const conversation = await apiService.get<ConversationResource>(
      API_CONFIG.ENDPOINTS.ADVICE.GET_ADVICE(id),
      session
    );
    if (!conversation) {
      logger.warn('[AdviceService] Conversation not found', { id, duration: `${Date.now() - startTime}ms` });
      throw new Error(`Conversation with id ${id} not found`);
    }
    logger.info('[AdviceService] Conversation fetched successfully', {
      id,
      title: conversation.title,
      duration: `${Date.now() - startTime}ms`
    });
    return conversation;
  }

  /**
   * Creates a new conversation.
   *
   * @param {Object} data - Data for the new conversation.
   * @param {string} data.title - The title of the conversation.
   * @param {string} data.description - The description.
   * @param {string} [data.formAnswerId] - Optional form answer ID.
   * @param {Session} session - The user session.
   * @returns {Promise<Advice>} The created advice.
   */
  async create(
    data: { title: string; description: string; formAnswerId?: string },
    session: Session
  ): Promise<Advice> {
    const startTime = Date.now();
    logger.info('[AdviceService] Creating new conversation', { title: data.title });
    const key = `create-${JSON.stringify(data)}`;
    const requestData = {
      title: data.title,
      description: data.description,
      ...(data.formAnswerId ? { formAnswerId: data.formAnswerId } : {}),
    };
    try {
      const result = await this.dedupeRequest(
        key,
        apiService.post<Advice>(
          API_CONFIG.ENDPOINTS.ADVICE.BASE,
          requestData,
          session
        )
      );
      logger.info('[AdviceService] Conversation created successfully', {
        id: result.id,
        duration: `${Date.now() - startTime}ms`
      });
      return result;
    } catch (error: any) {
      logger.error('[AdviceService] Create conversation failed', {
        error: error.message,
        duration: `${Date.now() - startTime}ms`
      });
      throw error;
    }
  }

  /**
   * Creates an advice item.
   *
   * @param {string} id - The conversation ID.
   * @param {Object} data - Data containing the question.
   * @param {Session} session - The user session.
   * @returns {Promise<AdviceItem>} The created advice item.
   */
  async createItem(
    id: string,
    data: { question: string },
    session: Session
  ): Promise<AdviceItem> {
    const startTime = Date.now();
    logger.info('[AdviceService] Creating advice item', { conversationId: id });
    const key = `createItem-${id}-${JSON.stringify(data)}`;
    try {
      const result = await this.dedupeRequest(
        key,
        apiService.post<AdviceItem>(
          API_CONFIG.ENDPOINTS.ADVICE.ITEMS(id),
          data,
          session
        )
      );
      logger.info('[AdviceService] Advice item created successfully', {
        id: result.id,
        duration: `${Date.now() - startTime}ms`
      });
      return result;
    } catch (error: any) {
      logger.error('[AdviceService] Create advice item failed', {
        id,
        error: error.message,
        duration: `${Date.now() - startTime}ms`
      });
      throw error;
    }
  }

  /**
   * Deletes a conversation.
   *
   * @param {string} id - The conversation ID.
   * @param {Session} session - The user session.
   * @returns {Promise<void>}
   */
  async delete(id: string, session: Session): Promise<void> {
    const startTime = Date.now();
    logger.info('[AdviceService] Deleting conversation', { id });
    const key = `delete-${id}`;

    try {
      // Check if there's a pending request
      const pendingRequest = this.pendingRequests.get(key);
      if (pendingRequest) {
        logger.debug('[AdviceService] Deletion already in progress', { id });
        return pendingRequest;
      }

      // Create and track the delete request
      const deleteRequest = apiService.delete<void>(
        API_CONFIG.ENDPOINTS.ADVICE.DELETE(id),
        session
      );
      this.pendingRequests.set(key, deleteRequest);

      // Execute the request
      await deleteRequest;

      // Clear any cached data
      this.cache.delete(this.getCacheKey(`list`));
      this.cache.delete(this.getCacheKey(`conversation-${id}`));

      // Cleanup
      this.pendingRequests.delete(key);
      logger.info('[AdviceService] Conversation deleted successfully', {
        id,
        duration: `${Date.now() - startTime}ms`
      });
    } catch (error: any) {
      // Cleanup on error
      this.pendingRequests.delete(key);
      logger.error('[AdviceService] Delete conversation failed', {
        id,
        error: error.message,
        duration: `${Date.now() - startTime}ms`
      });
      throw error;
    }
  }

  /**
   * Updates the title of a conversation.
   *
   * @param {string} id - The conversation ID.
   * @param {string} title - The new title.
   * @param {Session} session - The user session.
   * @returns {Promise<Advice>} The updated advice.
   */
  async updateTitle(
    id: string,
    title: string,
    session: Session
  ): Promise<Advice> {
    const startTime = Date.now();
    logger.info('[AdviceService] Updating conversation title', { id, title });
    const key = `updateTitle-${id}-${title}`;
    try {
      const result = await this.dedupeRequest(
        key,
        apiService.patch<Advice>(
          API_CONFIG.ENDPOINTS.ADVICE.TITLE(id),
          { title },
          session
        )
      );
      logger.info('[AdviceService] Conversation title updated successfully', {
        id,
        duration: `${Date.now() - startTime}ms`
      });
      return result;
    } catch (error: any) {
      logger.error('[AdviceService] Update title failed', {
        id,
        error: error.message,
        duration: `${Date.now() - startTime}ms`
      });
      throw error;
    }
  }

  /**
   * Retrieves form answers for a conversation.
   *
   * @param {string} id - The conversation ID.
   * @param {Session} session - The user session.
   * @returns {Promise<Record<string, any>>} The form answers.
   */
  async getFormAnswers(
    id: string,
    session: Session
  ): Promise<Record<string, any>> {
    const startTime = Date.now();
    logger.info('[AdviceService] Fetching form answers', { id });
    const key = `getFormAnswers-${id}`;
    try {
      const result = await this.dedupeRequest(
        key,
        apiService.get<Record<string, any>>(
          API_CONFIG.ENDPOINTS.ADVICE.FORM_ANSWERS(id),
          session
        )
      );
      logger.info('[AdviceService] Form answers fetched successfully', {
        id,
        duration: `${Date.now() - startTime}ms`
      });
      return result;
    } catch (error: any) {
      logger.error('[AdviceService] Get form answers failed', {
        id,
        error: error.message,
        duration: `${Date.now() - startTime}ms`
      });
      throw error;
    }
  }

  async getConversationHistory(chatId: string, session: Session) {
    const res = await apiService.get<any>(
      API_CONFIG.ENDPOINTS.ADVICE.HISTORY(chatId),
      session
    );
    logger.info('[AdviceService] Conversation history:', res);
    return res;
  }

  /**
   * Updates form answers for a conversation.
   *
   * @param {string} id - The conversation ID.
   * @param {QuestionnaireFormData} formAnswers - The new form answers.
   * @param {Session} session - The user session.
   * @returns {Promise<Record<string, any>>} The updated form answers.
   */
  async updateFormAnswers(
    id: string,
    formAnswers: FormData,
    session: Session
  ): Promise<Record<string, any>> {
    const startTime = Date.now();
    logger.info('[AdviceService] Updating form answers', { id });
    const key = `updateFormAnswers-${id}-${JSON.stringify(formAnswers)}`;
    try {
      const result = await this.dedupeRequest(
        key,
        apiService.put<Record<string, any>>(
          API_CONFIG.ENDPOINTS.ADVICE.FORM_ANSWERS(id),
          { formAnswers },
          session
        )
      );
      logger.info('[AdviceService] Form answers updated successfully', {
        id,
        duration: `${Date.now() - startTime}ms`
      });
      return result;
    } catch (error: any) {
      logger.error('[AdviceService] Update form answers failed', {
        id,
        error: error.message,
        duration: `${Date.now() - startTime}ms`
      });
      throw error;
    }
  }

  /**
   * Submits feedback data.
   *
   * @param {AdviceFeedbackData} data - The feedback data.
   * @param {Session} session - The user session.
   * @returns {Promise<void>}
   */
  async submitFeedback(
    data: AdviceFeedbackData,
    session: Session
  ): Promise<void> {
    const startTime = Date.now();
    logger.info('[AdviceService] Submitting feedback', { email: data.email, reaction: data.reaction });
    const key = `submitFeedback-${JSON.stringify(data)}`;
    try {
      await this.dedupeRequest(
        key,
        apiService.post<void>(API_CONFIG.ENDPOINTS.ADVICE.FEEDBACK, data, session)
      );
      logger.info('[AdviceService] Feedback submitted successfully', {
        email: data.email,
        duration: `${Date.now() - startTime}ms`
      });
    } catch (error: any) {
      logger.error('[AdviceService] Submit feedback failed', {
        email: data.email,
        error: error.message,
        duration: `${Date.now() - startTime}ms`
      });
      throw error;
    }
  }

  /**
   * Generates a response for a prompt.
   *
   * @param {Object} params - The parameters for generating a response.
   * @param {string} [params.id] - Optional conversation ID for existing chats.
   * @param {string} params.prompt - The prompt text.
   * @param {FormData} [params.formData] - Optional form data.
   * @param {AbortSignal} [params.signal] - Optional abort signal.
   * @param {Session} params.session - The user session.
   * @returns {Promise<AdviceItem>} The generated response.
   */
  async generatePromptResponse({
    id,
    prompt,
    formData,
    signal,
    session,
  }: {
    id?: string;
    prompt: string;
    formData?: FormData;
    signal?: AbortSignal;
    session: Session;
  }): Promise<AdviceItem> {
    if (!session?.user?.accessToken) {
      throw new Error('Unauthorized');
    }

    try {
      if (id) {
        // Create new item in existing conversation
        const response = await this.createItem(
          id,
          { question: prompt },
          session
        );
        if (!response) {
          throw new Error('Failed to create advice item');
        }
        return response;
      } else {
        // Create new conversation
        const newAdvice = await this.create(
          { title: prompt, description: '' },
          session
        );
        if (!newAdvice) {
          throw new Error('Failed to create new advice');
        }

        // Create first item
        const response = await this.createItem(
          newAdvice.id,
          { question: prompt },
          session
        );
        if (!response) {
          throw new Error('Failed to create advice item');
        }
        return response;
      }
    } catch (error: any) {
      logger.warn('[AdviceService] Generate response failed:', {
        error: error.message,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Updates a conversation title.
   *
   * @param {Object} params - The parameters for updating the title.
   * @param {string} params.id - The conversation ID.
   * @param {string} params.title - The new title.
   * @param {Session} params.session - The user session.
   * @returns {Promise<Advice>} The updated conversation.
   */
  async updateConversation({
    id,
    title,
    session,
  }: {
    id: string;
    title: string;
    session: Session;
  }): Promise<Advice> {
    if (!session?.user?.accessToken) {
      throw new Error('Unauthorized');
    }

    try {
      return await this.updateTitle(id, title, session);
    } catch (error: any) {
      logger.warn('[AdviceService] Update failed:', {
        id,
        error: error.message,
        status: error.response?.status,
      });
      throw error;
    }
  }

  /**
   * Creates a new advice item in a conversation.
   *
   * @param {string} conversationId - The conversation ID.
   * @param {string} question - The question text.
   * @param {Session} session - The user session.
   * @returns {Promise<Advice | null>} The created advice.
   */
  async createAdvice(
    conversationId: string,
    question: string,
    session: Session
  ): Promise<Advice | null> {
    return apiService.post<Advice>(
      API_CONFIG.ENDPOINTS.ADVICE.CREATE_ADVICE(conversationId),
      { question },
      session
    );
  }

  /**
   * Updates an advice item with a response.
   *
   * @param {string} adviceId - The advice ID.
   * @param {any} response - The response data.
   * @param {Session} session - The user session.
   * @returns {Promise<any>} The updated advice.
   */
  async updateAdvice(
    adviceId: string,
    response: any,
    session: Session
  ): Promise<any> {
    const startTime = Date.now();
    try {
      let processedResponse = response;
      let sources = Array.isArray(processedResponse?.sources)
        ? processedResponse.sources
        : [];

      const requestPayload = {
        status: processedResponse?.status,
        response: {
          ...processedResponse,
          sources: sources,
        },
      };

      logger.info('[AdviceService] Sending final payload to store-advice:', {
        adviceId,
        status: requestPayload.status,
        hasSources: Array.isArray(requestPayload.response.sources),
        sourceCount: Array.isArray(requestPayload.response.sources)
          ? requestPayload.response.sources.length
          : 0,
        sourceTypes: Array.isArray(requestPayload.response.sources)
          ? [
              ...new Set(
                requestPayload.response.sources.map((s: any) => s.type)
              ),
            ]
          : [],
      });

      const result = await apiService.post<any>(
        API_CONFIG.ENDPOINTS.ADVICE.STORE_ADVICE(adviceId),
        requestPayload,
        session
      );

      // If status is provided, also update it explicitly via the dedicated endpoint
      // to ensure it's saved correctly even if store-advice only updates the response column
      if (processedResponse?.status) {
        try {
          await this.updateAdviceItemStatus(
            adviceId,
            processedResponse.status,
            session
          );
          logger.debug('[AdviceService] Explicit status update successful', {
            adviceId,
            status: processedResponse.status,
          });
        } catch (statusError) {
          logger.warn('[AdviceService] Background status update failed', {
            adviceId,
            statusError,
          });
        }
      }

      logger.info('[AdviceService] updateAdvice completed successfully', {
        adviceId,
        duration: `${Date.now() - startTime}ms`,
      });

      return result;
    } catch (error) {
      logger.error('[AdviceService] updateAdvice failed:', {
        adviceId,
        error: (error as Error).message,
        duration: `${Date.now() - startTime}ms`,
      });
      throw error;
    }
  }

  /**
   * Get an AI advice response using the Bedrock Client.
   *
   * @param {string} prompt - The prompt to send to Bedrock.
   * @param {string} question - The original user question.
   * @param {string} chatId - The chat ID.
   * @param {string} lang - The language code.
   * @param {AbortSignal} [signal] - Optional abort signal.
   * @param {Function} [onChunk] - Optional chunk handler.
   * @param {Session} [session] - Optional user session.
   * @param {string} [adviceId] - Optional advice ID.
   * @returns {Promise<any>} The advice response.
   */
  async getAdvice(
    prompt: string,
    question: string,
    chatId: string,
    lang: string,
    signal?: AbortSignal,
    onChunk?: (chunk: any) => void,
    session?: Session,
    adviceId?: string
  ): Promise<any> {
    const key = `getAdvice-${chatId}-${prompt.substring(0, 50)}-${adviceId || ''}-streaming`;
    return this.dedupeRequest(
      key,
      this._performGetAdvice({
        chatId,
        adviceId,
        prompt,
        question,
        lang,
        signal,
        onChunk,
        session,
      })
    );
  }

  /**
   * Private method to perform the actual API call for getting advice.
   */
  private async _performGetAdvice({
    chatId,
    adviceId,
    prompt,
    question,
    lang,
    signal,
    onChunk,
    session,
  }: {
    chatId: string;
    adviceId?: string;
    prompt: string;
    question: string;
    lang: string;
    signal?: AbortSignal;
    onChunk?: (chunk: any) => void;
    session?: Session;
  }): Promise<any> {
    const startTime = Date.now();
    const controller = signal ? undefined : new AbortController();
    const requestSignal = signal || controller?.signal;

    const requestId =
      adviceId ||
      `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // Use adviceId if available, otherwise generate one for tracking
    const trackingId = adviceId || requestId;

    if (controller) {
      this.activeStreams.set(trackingId, {
        controller,
        cleanup: () => {
          this.activeStreams.delete(trackingId);
        },
      });
    }

    trackMetricsEvent({
      category: MetricsCategory.CONVERSATION,
      action: MetricsAction.QUESTION_ASKED,
      label: question,
      value: question.length,
      dimensions: {
        questionLength: question.length,
        lang,
        chatId,
      },
      userId: session?.user?.id,
    });

    // Initialize the response structure
    let finalResponse: any = {
      id: adviceId || '', // Use provided adviceId or empty string
      question: question,
      advice: '',
      status: 'STREAMING' as AdviceItemStatus, // Start with streaming status
      sources: [], // Initialize sources array
      // Initialize potential metadata fields - may only be populated by fallback
      urls: [],
      page_source: [],
      page_title: [],
      // ... other metadata fields initialized as empty arrays ...
      ccqData: [],
      relevant_forms: {
        // Initialize relevant_forms structure
        page_url: [],
        doc_url: [],
        filename: [],
        title: [],
        content: [],
        filled_by: [],
      },
    };

    try {
      logger.info(`[AdviceService] Starting advice generation process`, {
        chatId,
        adviceId,
        requestId: trackingId,
        questionLength: question.length,
        contextLength: prompt.length,
      });

      logger.info('[AdviceService] Calling Bedrock directly for advice', {
        requestId: trackingId,
      });

      // Determine dynamic prompt (only needed for direct call)
      // const dynamicSystemPrompt = createStructuredOutputPrompt(prompt) as string;
      const dynamicSystemPrompt = prompt as string;

      // Get the Cognito token from the session - use idToken for Cognito Identity Pool
      // The idToken contains the aud claim required by Cognito Identity Pool
      const cognitoToken = session?.user?.idToken || session?.user?.accessToken;

      logger.info(
        '[AdviceService] Using Cognito token for Bedrock authentication',
        {
          hasToken: !!cognitoToken,
          tokenType: session?.user?.idToken ? 'idToken' : 'accessToken',
          tokenLength: cognitoToken?.length || 0,
        }
      );

      const bedrockOptions: BedrockOptions = {
        // modelId: 'us.anthropic.claude-3-5-sonnet-20240620-v1:0', // Consider making this configurable
        dynamicSystemPrompt: dynamicSystemPrompt,
        temperature: 0.0,
        maxTokens: 8192, // Supports longer responses with inline citations
        useStructuredOutput: true,
        requestTimeout: 60000,
        apiKey: cognitoToken, // Pass the Cognito token to be used for authentication
      };

      if (onChunk) {
        logger.info('[AdviceService] Initiating streaming response from Bedrock', {
          requestId: trackingId,
        });
        // Streaming direct call
        const stream = await bedrockService.streamBedrockResponse(
          question, // Pass the actual question to Bedrock
          bedrockOptions
        );

        const reader = stream.getReader();
        const decoder = new TextDecoder();
        let accumulatedText = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunkText = decoder.decode(value, { stream: true });
            accumulatedText += chunkText;

            // Store markdown content in session storage (optional)
            try {
              if (
                typeof window !== 'undefined' &&
                accumulatedText.trim().length > 10
              ) {
                const storageKey = `md-${question.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}`;
                sessionStorage.setItem(storageKey, accumulatedText);
              }
            } catch (e) {
              /* Ignore storage errors */
            }

            // Send chunk notification - Include existing metadata from finalResponse
            onChunk({
              type: 'advice',
              response: {
                // Send response as an object
                advice: accumulatedText, // Send accumulated text
                // Include metadata available *before* the stream started
                // We send the sources array directly here.
                sources: finalResponse.sources,
                // Also include legacy flat arrays for compatibility if needed by UI during streaming
                urls: finalResponse.urls,
                page_source: finalResponse.page_source,
                page_title: finalResponse.page_title,
                urls_ccq: finalResponse.urls_ccq,
                page_source_ccq: finalResponse.page_source_ccq,
                page_title_ccq: finalResponse.page_title_ccq,
                texts_ccq: finalResponse.texts_ccq,
                modifications_ccq: finalResponse.modifications_ccq,
                urls_ccq_modif: finalResponse.urls_ccq_modif,
                page_source_ccq_modif: finalResponse.page_source_ccq_modif,
                page_title_ccq_modif: finalResponse.page_title_ccq_modif,
                texts_ccq_modif: finalResponse.texts_ccq_modif,
                caselaw_reference: finalResponse.caselaw_reference,
                caselaw_titre: finalResponse.caselaw_titre,
                caselaw_lienURL: finalResponse.caselaw_lienURL,
                caselaw_nomsDesPartis: finalResponse.caselaw_nomsDesPartis,
                relevant_forms: finalResponse.relevant_forms,
              },
              chunk: chunkText,
              requestId: trackingId,
              isDirectStream: true, // Indicate it's direct from Bedrock via service
              preserveMarkdown: true,
              status: 'streaming',
            });
          }

          finalResponse.advice = accumulatedText;
          finalResponse.status = 'completed';

          logger.info('[AdviceService] Stream processing finished', {
            requestId: trackingId,
            accumulatedLength: accumulatedText.length,
            duration: `${Date.now() - startTime}ms`,
          });

          // Send final chunk notification
          onChunk({
            type: 'advice',
            response: {
              // Send final response as an object
              advice: finalResponse.advice,
              sources: finalResponse.sources, // Ensure final sources are included
              // Include legacy flat arrays if necessary
              urls: finalResponse.urls,
              page_source: finalResponse.page_source,
              page_title: finalResponse.page_title,
              urls_ccq: finalResponse.urls_ccq,
              page_source_ccq: finalResponse.page_source_ccq,
              page_title_ccq: finalResponse.page_title_ccq,
              texts_ccq: finalResponse.texts_ccq,
              modifications_ccq: finalResponse.modifications_ccq,
              urls_ccq_modif: finalResponse.urls_ccq_modif,
              page_source_ccq_modif: finalResponse.page_source_ccq_modif,
              page_title_ccq_modif: finalResponse.page_title_ccq_modif,
              texts_ccq_modif: finalResponse.texts_ccq_modif,
              caselaw_reference: finalResponse.caselaw_reference,
              caselaw_titre: finalResponse.caselaw_titre,
              caselaw_lienURL: finalResponse.caselaw_lienURL,
              caselaw_nomsDesPartis: finalResponse.caselaw_nomsDesPartis,
              relevant_forms: finalResponse.relevant_forms,
            },
            isFinalChunk: true,
            requestId: trackingId,
            preserveMarkdown: true,
            status: 'completed',
          });

          // Store final markdown (optional)
          try {
            if (
              typeof window !== 'undefined' &&
              finalResponse.advice.trim().length > 10
            ) {
              const storageKey = `md-${question.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}`;
              sessionStorage.setItem(storageKey, finalResponse.advice);
            }
          } catch (e) {
            /* Ignore storage errors */
          }
        } catch (streamError) {
          logger.error(
            '[AdviceService] Error processing direct Bedrock stream:',
            {
              error: (streamError as Error).message,
              stack: (streamError as Error).stack,
              requestId: trackingId,
            }
          );
          finalResponse.advice = `Error: ${(streamError as Error).message}`;
          finalResponse.status = 'error';
          onChunk({
            type: 'advice',
            response: {
              // Send error response as an object
              advice: finalResponse.advice,
              sources: finalResponse.sources, // Include metadata even on error if available
              // Include legacy flat arrays if necessary
              urls: finalResponse.urls,
              page_source: finalResponse.page_source,
              page_title: finalResponse.page_title,
              urls_ccq: finalResponse.urls_ccq,
              page_source_ccq: finalResponse.page_source_ccq,
              page_title_ccq: finalResponse.page_title_ccq,
              texts_ccq: finalResponse.texts_ccq,
              modifications_ccq: finalResponse.modifications_ccq,
              urls_ccq_modif: finalResponse.urls_ccq_modif,
              page_source_ccq_modif: finalResponse.page_source_ccq_modif,
              page_title_ccq_modif: finalResponse.page_title_ccq_modif,
              texts_ccq_modif: finalResponse.texts_ccq_modif,
              caselaw_reference: finalResponse.caselaw_reference,
              caselaw_titre: finalResponse.caselaw_titre,
              caselaw_lienURL: finalResponse.caselaw_lienURL,
              caselaw_nomsDesPartis: finalResponse.caselaw_nomsDesPartis,
              relevant_forms: finalResponse.relevant_forms,
            },
            isFinalChunk: true,
            requestId: trackingId,
            status: 'error',
          });
        } finally {
          reader.releaseLock();
        }
      } else {
        // Non-streaming direct call
        const bedrockResponse = await bedrockService.getBedrockResponse(
          question, // Pass the actual question
          bedrockOptions
        );

        // Update final response
        finalResponse.id = bedrockResponse.id || finalResponse.id;
        finalResponse.advice = bedrockResponse.advice || '';
        finalResponse.status =
          (bedrockResponse.status as AdviceItemStatus) || 'COMPLETED';

        // IMPORTANT: Ensure sources from metadata are preserved
        // The bedrockResponse itself likely won't have sources, use what we already processed
        // finalResponse.sources = finalResponse.sources; // Already set

        if (bedrockResponse.reasoning) {
          finalResponse.reasoning = bedrockResponse.reasoning;
        }

        // Store markdown (optional)
        try {
          if (
            typeof window !== 'undefined' &&
            finalResponse.advice.trim().length > 10
          ) {
            const storageKey = `md-${question.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}`;
            sessionStorage.setItem(storageKey, finalResponse.advice);
          }
        } catch (e) {
          /* Ignore storage errors */
        }
      }

      if (this.activeStreams.has(trackingId)) {
        const { cleanup } = this.activeStreams.get(trackingId)!;
        cleanup();
      }

      logger.info('[AdviceService] Advice retrieval completed', {
        requestId: trackingId,
        status: finalResponse.status,
      });
      return finalResponse;
    } catch (error: any) {
      logger.error('[AdviceService] _performGetAdvice failed:', {
        error: error.message,
        stack: error.stack,
        requestId: trackingId,
      });

      if (this.activeStreams.has(trackingId)) {
        const { cleanup } = this.activeStreams.get(trackingId)!;
        cleanup();
      }

      // Ensure finalResponse reflects the error state
      finalResponse.advice = `Error: ${error.message}`;
      finalResponse.status = 'error';

      // Send error via onChunk if available
      if (onChunk) {
        onChunk({
          type: 'advice',
          response: {
            // Send error response as an object
            advice: finalResponse.advice,
            sources: finalResponse.sources, // Include metadata even on error if available
            // Include legacy flat arrays if necessary
            urls: finalResponse.urls,
            page_source: finalResponse.page_source,
            page_title: finalResponse.page_title,
            urls_ccq: finalResponse.urls_ccq,
            page_source_ccq: finalResponse.page_source_ccq,
            page_title_ccq: finalResponse.page_title_ccq,
            texts_ccq: finalResponse.texts_ccq,
            modifications_ccq: finalResponse.modifications_ccq,
            urls_ccq_modif: finalResponse.urls_ccq_modif,
            page_source_ccq_modif: finalResponse.page_source_ccq_modif,
            page_title_ccq_modif: finalResponse.page_title_ccq_modif,
            texts_ccq_modif: finalResponse.texts_ccq_modif,
            caselaw_reference: finalResponse.caselaw_reference,
            caselaw_titre: finalResponse.caselaw_titre,
            caselaw_lienURL: finalResponse.caselaw_lienURL,
            caselaw_nomsDesPartis: finalResponse.caselaw_nomsDesPartis,
            relevant_forms: finalResponse.relevant_forms,
          },
          isFinalChunk: true,
          requestId: trackingId,
          status: 'error',
        });
      }

      return finalResponse; // Return the error state
    }
  }

  /**
   * Process metadata objects (from Lambda or other sources) and add them
   * to the targetObject's `sources` array.
   * Modifies the targetObject directly.
   */
  private processMetadata(targetObject: any, metadata: any): void {
    // Ensure sources array exists on the target object
    if (!Array.isArray(targetObject.sources)) {
      targetObject.sources = [];
    }

    // Use a Set to track unique source keys added in this processing run
    const addedSourceKeys = new Set<string>();

    // Helper to add a source if its unique key hasn't been added yet
    const addUniqueSource = (newSource: any) => {
      if (!newSource || !newSource.type) return;

      let uniqueKey: string | null = null;
      switch (newSource.type) {
        case 'INFO':
          uniqueKey = newSource.url;
          break;
        case 'FORM':
          uniqueKey = newSource.docUrl || newSource.pageUrl;
          break;
        case 'LAW':
        case 'MODIFIED_LAW':
          // Use type + URL + title for Laws
          uniqueKey = `${newSource.type}-${newSource.url}-${newSource.pageTitle || newSource.title}`;
          break;
        case 'CASELAW':
          uniqueKey = newSource.reference;
          break;
      }

      if (uniqueKey && !addedSourceKeys.has(uniqueKey)) {
        targetObject.sources.push(newSource);
        addedSourceKeys.add(uniqueKey);
      }
    };

    // Process CASELAW
    if (
      metadata?.caselaw?.reference &&
      Array.isArray(metadata.caselaw.reference)
    ) {
      metadata.caselaw.reference.forEach((ref: string, index: number) => {
        addUniqueSource({
          type: 'CASELAW',
          reference: ref,
          title: metadata.caselaw.titre?.[index] || ref,
          url: metadata.caselaw.lienURL?.[index] || '',
          nomsDesPartis: metadata.caselaw.nomsDesPartis?.[index] || '',
        });
      });
    }

    // Process INFO
    if (metadata?.infos?.urls && Array.isArray(metadata.infos.urls)) {
      metadata.infos.urls.forEach((url: string, i: number) => {
        addUniqueSource({
          type: 'INFO',
          url: url,
          pageSource: metadata.infos.page_source?.[i] || '',
          pageTitle: metadata.infos.page_title?.[i] || '',
        });
      });
    }

    // Process FORM
    if (metadata?.relevant_forms) {
      const forms = metadata.relevant_forms;
      const pageUrls = Array.isArray(forms.page_url) ? forms.page_url : [];
      const docUrls = Array.isArray(forms.doc_url) ? forms.doc_url : [];
      const formCount = Math.max(pageUrls.length, docUrls.length);

      if (formCount > 0) {
        for (let i = 0; i < formCount; i++) {
          // Prefer docUrl, fallback to pageUrl
          const primaryUrl = docUrls[i] || pageUrls[i];
          if (primaryUrl) {
            // Only add if we have at least one URL
            addUniqueSource({
              type: 'FORM',
              title: forms.title?.[i] || forms.filename?.[i] || '',
              content: forms.content?.[i] || '',
              pageUrl: pageUrls[i] || '',
              docUrl: docUrls[i] || '',
              filename: forms.filename?.[i] || '',
              filledBy: forms.filled_by?.[i] || [],
            });
          }
        }
      }
    }

    // Process LAW
    if (metadata?.laws?.urls && Array.isArray(metadata.laws.urls)) {
      metadata.laws.urls.forEach((url: string, index: number) => {
        const isModified = metadata.laws.modifications?.[index] === true;
        addUniqueSource({
          type: 'LAW',
          url: url,
          title: metadata.laws.page_title?.[index] || '', // Use title from lambda
          pageTitle: metadata.laws.page_title?.[index] || '', // Keep legacy field if needed
          pageSource: metadata.laws.page_source?.[index] || '',
          content: metadata.laws.texts?.[index] || '', // Use text from lambda
          isModified: isModified,
        });
      });
    }

    // Process MODIFIED_LAW
    // Note: Lambda response structure for modified_laws seems sparse in the example.
    // Assuming it mirrors the structure of 'laws' if populated.
    if (
      metadata?.modified_laws?.urls &&
      Array.isArray(metadata.modified_laws.urls)
    ) {
      metadata.modified_laws.urls.forEach((url: string, index: number) => {
        // Only add if there's actual text content
        const modText = metadata.modified_laws.texts?.[index];
        if (url && modText) {
          addUniqueSource({
            type: 'MODIFIED_LAW',
            url: url,
            title: metadata.modified_laws.page_title?.[index] || '',
            pageTitle: metadata.modified_laws.page_title?.[index] || '',
            pageSource: metadata.modified_laws.page_source?.[index] || '',
            content: modText,
            isModified: true,
          });
        }
      });
    }

    logger.debug(
      '[AdviceService:processMetadata] Processed metadata into sources:',
      {
        targetObjectId: targetObject.id, // Assuming targetObject has an ID
        finalSourceCount: targetObject.sources.length,
        finalSourceTypes: [
          ...new Set(targetObject.sources.map((s: any) => s.type)),
        ],
      }
    );
  }

  /**
   * Helper method to count occurrences of a character in a string
   */
  private countOccurrences(str: string, char: string): number {
    return (str.match(new RegExp(char, 'g')) || []).length;
  }

  /**
   * Helper function to update response object with JSON data.
   *
   * @param {any} responseObj - The response object to update.
   * @param {any} jsonData - The JSON data to update with.
   */
  private updateResponseWithJson(
    responseObj: any,
    jsonData: Record<string, any> | string
  ): void {
    // Handle plain text responses from preprocessor
    if (typeof jsonData === 'string') {
      responseObj.advice = jsonData;
      responseObj.status = 'ready';
      return;
    }

    const copyField = (fieldName: string) => {
      if (
        jsonData &&
        typeof jsonData === 'object' &&
        jsonData[fieldName] !== undefined
      ) {
        responseObj[fieldName] = jsonData[fieldName];
      }
    };

    const copyArrayField = (fieldName: string) => {
      if (Array.isArray(jsonData[fieldName])) {
        responseObj[fieldName] = jsonData[fieldName];
      }
    };

    copyField('id');
    copyField('question');
    copyField('advice');

    // Prioritize copying the sources array
    copyArrayField('sources');

    // Copy legacy flat arrays
    copyArrayField('urls');
    copyArrayField('page_source');
    copyArrayField('page_title');
    copyArrayField('urls_ccq');
    copyArrayField('page_source_ccq');
    copyArrayField('page_title_ccq');
    copyArrayField('texts_ccq');
    copyArrayField('modifications_ccq');
    copyArrayField('urls_ccq_modif');
    copyArrayField('page_source_ccq_modif');
    copyArrayField('page_title_ccq_modif');
    copyArrayField('texts_ccq_modif');
    copyArrayField('caselaw_reference');
    copyArrayField('caselaw_titre');
    copyArrayField('caselaw_lienURL');
    copyArrayField('caselaw_nomsDesPartis');
    copyArrayField('caselaw_texte');

    // Copy relevant_forms object
    copyField('relevant_forms');
  }

  /**
   * Calls the Lambda API endpoint for getting advice.
   *
   * @param {string} question - The question text.
   * @param {string} chatId - The chat ID.
   * @param {string} lang - The language code.
   * @param {FormAnswer} formAnswer - The form answer data.
   * @param {Session} [session] - Optional user session.
   * @returns {Promise<any>} The advice response.
   */
  async getLambdaPrompt(
    chatId: string,
    adviceId: string,
    question: string,
    lang: string,
    formAnswer: FormAnswer,
    conversationHistory: any[],
    session?: Session,
    abortSignal?: AbortSignal
  ): Promise<any> {
    const requestId = `lambda-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    logger.info('[AdviceService] Calling Lambda Prompt API', {
      requestId,
      chatId,
      adviceId,
      question:
        question.substring(0, 100) + (question.length > 100 ? '...' : ''),
    });

    trackMetricsEvent({
      category: MetricsCategory.CONVERSATION,
      action: MetricsAction.LAMBDA_PROMPT_ASKED,
      label: question,
      value: question.length,
      dimensions: {
        questionLength: question.length,
        lang,
        chatId,
      },
      userId: session?.user?.id,
    });

    try {
      const headers = new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      });

      if (session?.user?.accessToken) {
        headers.append('Authorization', `Bearer ${session.user.accessToken}`);
      }

      // Prepare request body with proper camelCase field names
      const requestBody = {
        conversationId: chatId,
        adviceId: adviceId,
        question,
        lang,
        formAnswer: formAnswer,
        history: conversationHistory,
      };

      logger.info('[AdviceService] Lambda request body:', {
        body: requestBody,
      });
      // Use the new Lambda API endpoint in the advice folder
      const response = await fetch('/api/advice/lambda', {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: abortSignal,
      });

       //response.status is 'blocked' if guardrails are triggered
      logger.info('[AdviceService] Lambda response:', {
        response: response,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[AdviceService] Lambda API error response:', {
          status: response.status,
          body: errorText,
          requestId,
        });
        throw new Error(`Lambda API Error (${response.status}): ${errorText}`);
      }

      const responseData = await response.json();
      logger.info('[AdviceService] Lambda API response received:', {
        requestId,
        responseSize: JSON.stringify(responseData).length,
        status: responseData.status || 'unknown',
      });

      // Process the Lambda response format to ensure it maps to what the UI expects
      this.processLambdaResponse(responseData);

      return responseData;
    } catch (error) {
      logger.info('[AdviceService] Lambda API request failed:', {
        error: (error as Error).message,
        stack: (error as Error).stack,
        requestId,
      });
      throw error;
    }
  }

  /**
   * Process Lambda response format to ensure all necessary fields for source rendering
   * are correctly mapped to the structure expected by the UI components.
   *
   * Modifies the responseData object directly.
   *
   * @param {any} responseData - The Lambda API response data.
   */
  private processLambdaResponse(responseData: any): void {
    // Skip processing if data is not in expected format or is an error
    if (
      !responseData ||
      typeof responseData !== 'object' ||
      responseData.status === 'error'
    ) {
      // Ensure sources is empty on error or invalid data
      if (responseData && typeof responseData === 'object') {
        responseData.sources = [];
      }
      logger.warn(
        '[AdviceService:processLambdaResponse] Skipping processing due to error or invalid data',
        { status: responseData?.status }
      );
      return;
    }

    logger.info(
      '[AdviceService] Processing Lambda response before metadata extraction:',
      {
        hasInfos: !!responseData.infos,
        hasLaws: !!responseData.laws,
        hasModifiedLaws: !!responseData.modified_laws,
        hasCaselaw: !!responseData.caselaw,
        hasRelevantForms: !!responseData.relevant_forms,
        status: responseData.status || 'unknown',
      }
    );

    // Strategy: Use Lambda's sources[] directly when it contains complete data
    // (citationIndex + full content). Fall back to processMetadata + legacy
    // arrays for old Lambda versions that don't send sources[].
    try {
      const lambdaSources: any[] = Array.isArray(responseData.sources)
        ? responseData.sources
        : [];

      if (lambdaSources.length > 0) {
        // --- NEW PATH: Lambda sends authoritative sources[] with full data ---
        // Normalize each source to ensure all expected fields exist.
        responseData.sources = lambdaSources.map((s: any) => ({
          type: s.type,
          citationIndex: s.citationIndex ?? null,
          url: s.url || null,
          pageSource: s.pageSource || null,
          pageTitle: s.pageTitle || null,
          title: s.title || s.pageTitle || null,
          content: s.content || null,
          filename: s.filename || null,
          isModified: s.isModified ?? false,
          modification: s.modification && typeof s.modification === 'object' ? s.modification : null,
          docUrl: s.docUrl || null,
          pageUrl: s.pageUrl || null,
          reference: s.reference || null,
          nomsDesPartis: s.nomsDesPartis || null,
          filledBy: Array.isArray(s.filledBy) ? s.filledBy : [],
        }));
      } else {
        // --- LEGACY FALLBACK: No sources[]; build from parallel arrays ---
        responseData.sources = [];
        this.processMetadata(responseData, {
          infos: responseData.infos,
          laws: responseData.laws,
          modified_laws: responseData.modified_laws,
          caselaw: responseData.caselaw,
          relevant_forms: responseData.relevant_forms,
        });
      }

      logger.debug('[AdviceService] Processed Lambda response data:', {
        path: lambdaSources.length > 0 ? 'NEW_SOURCES' : 'LEGACY_ARRAYS',
        sourceCount: responseData.sources.length,
        sourcesWithCitation: responseData.sources.filter(
          (s: any) => s.citationIndex != null
        ).length,
        sourceTypes: [
          ...new Set(responseData.sources.map((s: any) => s.type)),
        ],
      });
    } catch (error) {
      logger.error(
        '[AdviceService:processLambdaResponse] Error processing metadata:',
        {
          error: (error as Error).message,
          stack: (error as Error).stack,
        }
      );
      // Ensure sources is empty on error during processing
      responseData.sources = [];
    }
  }

  /**
   * Updates the status of a specific advice item.
   *
   * @param {string} itemId - The ID of the advice item.
   * @param {AdviceItemStatus} status - The new status.
   * @param {Session} session - The user session.
   * @returns {Promise<void>}
   */
  async updateAdviceItemStatus(
    itemId: string,
    status: AdviceItemStatus,
    session: Session
  ): Promise<void> {
    logger.info('[AdviceService] Updating advice item status:', {
      itemId,
      status,
    });
    const key = `updateStatus-${itemId}-${status}`;
    return this.dedupeRequest(
      key,
      apiService.patch<void>(
        API_CONFIG.ENDPOINTS.ADVICE.ITEM_STATUS(itemId),
        { status },
        session
      )
    );
  }


  /**
   * Gets the status of a specific advice item.
   *
   * @param {string} itemId - The ID of the advice item.
   * @param {Session} session - The user session.
   * @returns {Promise<{ status: AdviceItemStatus }>}
   */
  async getAdviceItemStatus(
    itemId: string,
    session: Session
  ): Promise<{ status: AdviceItemStatus }> {
    const key = `getStatus-${itemId}`;
    return this.dedupeRequest(
      key,
      apiService.get<{ status: AdviceItemStatus }>(
        API_CONFIG.ENDPOINTS.ADVICE.ITEM_STATUS(itemId),
        session
      )
    );
  }

  /**
   * Increments the click count for a specific advice source.
   *
   * @param {string} sourceId - The ID of the advice source.
   * @param {Session} session - The user session
   * @returns {Promise<void>} Resolves when the API call succeeds.
   */
  async incrementAdviceSourceClick(
    sourceId: string,
    session: Session | null
  ): Promise<void> {
    try {
      await apiService.post<void>(
        API_CONFIG.ENDPOINTS.ADVICE_SOURCE.CLICK(sourceId),
        {}, // no body needed
        session
      );
    } catch (err) {
      console.error('Failed to increment advice source click count', err);
    }
  }

  // ===== FOLLOW-UP QUESTIONS METHODS =====

  /**
   * Get follow-up questions for an advice item
   *
   * @param {string} adviceItemId - The advice item ID
   * @param {Session} session - The user session
   * @returns {Promise<FollowUpQuestion[]>} Array of follow-up questions
   */
  async getFollowUps(adviceItemId: string, session: Session): Promise<any[]> {
    try {
      const response = await apiService.get<any[]>(
        `${API_CONFIG.ENDPOINTS.ADVICE.BASE}/${adviceItemId}/follow-ups`,
        session
      );
      return response || [];
    } catch (error) {
      logger.error('[AdviceService] getFollowUps failed:', {
        adviceItemId,
        error: (error as Error).message,
      });
      return [];
    }
  }

  /**
   * Mark a follow-up question as used and get the question text
   *
   * @param {string} followUpId - The follow-up question ID
   * @param {Session} session - The user session
   * @returns {Promise<{success: boolean; question: string; followUpId: string}>} Response with question text for auto-submission
   */
  async useFollowUp(
    followUpId: string,
    session: Session
  ): Promise<{ success: boolean; question: string; followUpId: string }> {
    try {
      const response = await apiService.post<any>(
        `${API_CONFIG.ENDPOINTS.ADVICE.BASE}/follow-ups/${followUpId}/use`,
        {},
        session
      );
      return response;
    } catch (error) {
      logger.error('[AdviceService] useFollowUp failed:', {
        followUpId,
        error: (error as Error).message,
      });
      throw error;
    }
  }

  constructor() {
    // Constructor intentionally empty
  }
}

/**
 * Singleton instance of the AdviceService
 */
export const adviceService = new AdviceService();
